import { Plus, Trash2 } from "lucide-react";
import { Dispatch, SetStateAction, useState } from "react";
import { object, string } from "yup";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";

import { PermissionsKeys } from "components/permissions/permission-keys";
import { usePermissions } from "components/permissions/usePermissions";
import { Role } from "data/models";

interface MemberInvitationsProps {
  isOpen: boolean;
  isLoading: boolean;
  isError?: boolean;
  handleClose: () => void;
  handleSubmit: (itemList: InvitationItem[]) => void;
  items?: string[];
  orgName: string;
}

export interface InvitationItem {
  email: string;
  role: Role;
}

export const invitationValidator = object({
  email: string().email("Enter a valid email").required("Email is required"),
});

const hasDuplicateEmails = (itemList: InvitationItem[]): boolean => {
  const emails = itemList.map((i) => i.email);
  return new Set(emails).size !== emails.length;
};

const isInvalidEmail = (email: string): boolean => {
  if (!email) return true;
  const emailRegex = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
  return !emailRegex.test(email);
};

const isErrorList = (itemList: InvitationItem[]): boolean => {
  return (
    itemList.some((item) => isInvalidEmail(item.email)) ||
    hasDuplicateEmails(itemList)
  );
};

export const MemberInvitationModal = (props: MemberInvitationsProps) => {
  const [itemList, setItemList] = useState<InvitationItem[]>([
    { email: "", role: Role.ADMIN },
  ]);

  const { isPermitted } = usePermissions();

  const resetAndClose = () => {
    setItemList([{ email: "", role: Role.ADMIN }]);
    props.handleClose();
  };

  const addItem = (
    list: InvitationItem[],
    setList: Dispatch<SetStateAction<InvitationItem[]>>,
  ) => {
    if (list.length < 5) setList([...list, { email: "", role: Role.ADMIN }]);
  };

  const removeItem = (
    index: number,
    list: InvitationItem[],
    setList: Dispatch<SetStateAction<InvitationItem[]>>,
  ) => {
    if (list.length <= 1) return;
    const next = [...list];
    next.splice(index, 1);
    setList(next);
  };

  const updateEmail = (index: number, value: string) => {
    const next = [...itemList];
    next[index].email = value;
    setItemList(next);
  };

  const updateRole = (index: number, value: Role) => {
    const next = [...itemList];
    next[index].role = value;
    setItemList(next);
  };

  const roleDisabled = !isPermitted([PermissionsKeys.rolesCreate]);

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) resetAndClose();
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Invite to {props.orgName}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {itemList.map((item, index) => {
            const invalidEmail = isInvalidEmail(item.email);
            return (
              <div key={index} className="flex items-start gap-2">
                <Field className="flex-1">
                  <FieldLabel htmlFor={`email-${index}`}>Email</FieldLabel>
                  <Input
                    id={`email-${index}`}
                    type="email"
                    value={item.email}
                    aria-invalid={invalidEmail}
                    onChange={(e) => updateEmail(index, e.target.value)}
                  />
                  {invalidEmail && (
                    <FieldError>Please enter a valid email</FieldError>
                  )}
                </Field>
                <Field className="w-36">
                  <FieldLabel htmlFor={`role-${index}`}>Role</FieldLabel>
                  <Select
                    value={item.role}
                    onValueChange={(value) => updateRole(index, value as Role)}
                    disabled={roleDisabled}
                  >
                    <SelectTrigger id={`role-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(Role)
                        .filter((role) => role !== Role.OWNER)
                        .map((role) => (
                          <SelectItem key={role} value={role}>
                            {role.charAt(0).toUpperCase() + role.slice(1)}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Button
                  variant="ghost"
                  size="icon"
                  className="mt-6"
                  disabled={itemList.length <= 1}
                  onClick={() => removeItem(index, itemList, setItemList)}
                >
                  <Trash2 />
                </Button>
              </div>
            );
          })}

          <div>
            <Button
              variant="ghost"
              size="sm"
              disabled={
                itemList[itemList.length - 1].email === "" ||
                itemList.length >= 5
              }
              onClick={() => addItem(itemList, setItemList)}
            >
              <Plus />
              Add another
            </Button>
          </div>

          {props.isError && (
            <Alert variant="destructive">
              <AlertDescription>
                Something went wrong sending invitations. Please try again.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={resetAndClose}
            disabled={props.isLoading}
          >
            Cancel
          </Button>
          <Button
            disabled={isErrorList(itemList) || props.isLoading}
            onClick={() => {
              props.handleSubmit(itemList);
              setItemList([{ email: "", role: Role.ADMIN }]);
            }}
          >
            {props.isLoading ? (
              <>
                <Spinner />
                Invite
              </>
            ) : (
              "Invite"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
